import React, {useCallback, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {
    Alert,
    Box,
    Card,
    CardActionArea,
    CardContent,
    CircularProgress,
    Container,
    InputAdornment,
    Stack,
    Tab,
    Tabs,
    TextField,
    Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import {useUserAuth} from "../../context/UserAuthContext";
import {ProjectCardMedia} from "../common/ProjectImage";
import {
    getOtherProjects,
    getProjectsForCollege,
    getEnabledProjects,
    searchProjects,
} from "../../services/engineerService";
import PortalTopbar from "../Association/PortalTopbar";

const PRIMARY = "#3E4F88";

// Mesmas três listas da app ESFAPP (HomePage)
const TABS = [
    {key: "forYou", label: "Para ti"},
    {key: "all", label: "Todos"},
    {key: "others", label: "Outros"},
];

/**
 * Home do portal de engenheiros (Fase 2) — espelha a HomePage da app:
 *   - "Para ti": projetos do teu colégio (Projects.College == Course)
 *   - "Todos":   todos os projetos aprovados
 *   - "Outros":  projetos de outros colégios
 *   - pesquisa em título, associação, colégio e localização
 * Apenas projetos com Enabled=true aparecem (igual à app).
 */
const EngineerHome = () => {

    const {profile} = useUserAuth();
    const navigate = useNavigate();

    const [tab, setTab] = useState("forYou");
    const [projects, setProjects] = useState([]);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const course = profile && profile.Course ? profile.Course : "";

    const load = useCallback(async () => {
        setLoading(true);
        try {
            let result;
            if (search.trim()) {
                result = await searchProjects(search.trim());
            } else if (tab === "forYou") {
                result = await getProjectsForCollege(course);
            } else if (tab === "others") {
                result = await getOtherProjects(course);
            } else {
                result = await getEnabledProjects();
            }
            setProjects(result);
            setError("");
        } catch (err) {
            console.error("Erro ao carregar projetos:", err);
            setError("Não foi possível carregar os projetos. Tenta atualizar a página.");
        } finally {
            setLoading(false);
        }
    }, [tab, search, course]);

    useEffect(() => {
        load();
    }, [load]);

    const emptyMessage = search.trim()
        ? `Nenhum projeto encontrado para “${search.trim()}”.`
        : (tab === "forYou"
            ? "Ainda não há projetos aprovados para o teu colégio. Vê a lista “Todos” para veres o que está disponível."
            : tab === "others"
                ? "Ainda não há projetos aprovados de outros colégios."
                : "Ainda não há projetos aprovados na plataforma.");

    return (
        <Box minHeight="100vh" sx={{bgcolor: "#f4f5fa"}}>
            <PortalTopbar/>
            <Container maxWidth="lg" sx={{py: 5}}>
                <Stack spacing={3}>
                    <Box>
                        <Typography variant="h4" sx={{fontWeight: 700, color: PRIMARY}}>
                            {profile?.FirstName ? `Olá, ${profile.FirstName}` : "Olá"}
                        </Typography>
                        <Typography color="text.secondary">
                            Projetos de voluntariado na plataforma E4IU.
                        </Typography>
                    </Box>

                    {!search.trim() && (
                        <Tabs
                            value={tab}
                            onChange={(e, value) => setTab(value)}
                            sx={{
                                "& .MuiTab-root": {textTransform: "none", fontWeight: 600, minHeight: 48},
                                "& .Mui-selected": {color: PRIMARY},
                                "& .MuiTabs-indicator": {backgroundColor: PRIMARY},
                            }}
                        >
                            {TABS.map((t) => (
                                <Tab key={t.key} value={t.key} label={t.label}/>
                            ))}
                        </Tabs>
                    )}

                    <TextField
                        size="small"
                        placeholder="Pesquisar por título, associação, colégio ou localização…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action"/>
                                </InputAdornment>
                            ),
                        }}
                    />

                    {error && <Alert severity="error">{error}</Alert>}

                    {loading ? (
                        <Box sx={{display: "flex", justifyContent: "center", py: 6}}>
                            <CircularProgress/>
                        </Box>
                    ) : projects.length === 0 ? (
                        <Alert severity="info" sx={{alignItems: "flex-start", textAlign: "left"}}>
                            {emptyMessage}
                        </Alert>
                    ) : (
                        <Box
                            sx={{
                                display: "grid",
                                gap: 3,
                                gridTemplateColumns: {
                                    xs: "1fr",
                                    sm: "repeat(2, 1fr)",
                                    md: "repeat(3, 1fr)",
                                },
                            }}
                        >
                            {projects.map((p) => (
                                <Card key={p.key} sx={{height: "100%"}}>
                                    <CardActionArea onClick={() => navigate(`/app/engenheiro/projetos/${p.Id}`)} sx={{height: "100%"}}>
                                        <ProjectCardMedia src={p.Logo} alt={p.Title}/>
                                        <CardContent sx={{pb: 2}}>
                                            <Typography variant="h6" sx={{fontWeight: 700, color: PRIMARY, mb: 1}}>
                                                {p.Title}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{mb: 0.5}}>
                                                {p.Association}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{mb: 0.5}}>
                                                🎓 {p.College}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{mb: 0.5}}>
                                                📍 {p.Location}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {p.Time || "—"} · {p.Type || "—"}
                                            </Typography>
                                        </CardContent>
                                    </CardActionArea>
                                </Card>
                            ))}
                        </Box>
                    )}
                </Stack>
            </Container>
        </Box>
    );
};

export default EngineerHome;
